import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, Keyboard } from "react-native";
import i18n from "@microdoing/shared/i18n";
import {
  changeActionState,
  changeComment,
} from "@microdoing/shared/services/action/actions";
import {
  getAnalysisType,
  getComment,
} from "@microdoing/shared/services/action/selectors";
import {
  getDebriefText,
  getIsFeedbackRequired,
  getStepSelected,
  getTeam,
  getUserCampaignRank,
  getUserCampaignTeamRank,
} from "@microdoing/shared/services/userCampaign/selectors";
import {
  ArrowCircleButton,
  Box,
  MainButton,
  MainText,
  MainTitle,
  MySafeAreaView,
  PressableOpacity,
} from "app/components/common";
import theme from "app/Theme/Theme";
import { useDispatch, useSelector } from "react-redux";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import selectLanguage from "@microdoing/shared/utils/selectLanguage";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import { getFilesToUpload } from "@microdoing/shared/services/upload/selectors";
import { updateFilesToUpload } from "@microdoing/shared/services/upload/actions";
import { getUserExperience } from "@microdoing/shared/services/user/selectors";
import HeaderScreen from "app/components/common/HeaderScreen";
import AudioAnalysis from "./AudioAnalysis";
import TextAndImageAnalysis from "./TextAndImageAnalysis";
import VideoAnalysis from "./VideoAnalysis";
import { selectStep } from "@microdoing/shared/services/userCampaign/actions";
import { isWeb } from "@microdoing/shared/utils/deviceDetectors";
import { useBackHandler } from "app/hooks/useBackHandler";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebAnalysisBoxing from "app/components/common/Web/WebAnalysisBoxing";
import Action from "../Action/Action";
import { breadCrumbsReflexiveAnalysisScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import normalize from "app/utils/normalize";
import { useKeyboard } from "app/hooks/useKeyboardHeight";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import Animated, {
  interpolate,
  useAnimatedKeyboard,
  useAnimatedStyle,
} from "react-native-reanimated";

export interface IReflexiveAnalysisScreenProps {}

const ReanimatedBox = Animated.createAnimatedComponent(Box);

const ReflexiveAnalysisScreen = ({}: IReflexiveAnalysisScreenProps) => {
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [audioIsPlaying, setAudioIsPlaying] = useState(false);

  const stepSelected = useSelector(getStepSelected);
  const comment = useSelector(getComment);
  const language = useSelector(getLanguage);
  const filesToUpload = useSelector(getFilesToUpload);
  const team = useSelector(getTeam);
  const userPoints = useSelector(getUserExperience);
  const teamRank = useSelector(getUserCampaignTeamRank);
  const userRank = useSelector(getUserCampaignRank);
  const isFeedbackRequired = useSelector(getIsFeedbackRequired);
  const debriefText = useSelector(getDebriefText);
  const analysisType = useSelector(getAnalysisType);
  const isFocused = useIsFocused();
  const navigation = useNavigation();
  const keyboard = useAnimatedKeyboard();

  const numberOfFiles = useMemo(() => filesToUpload?.length || 0, [
    filesToUpload,
  ]);

  const {
    rating,
    totalDone,
    title,
    settings = {
      uploadInstruction: {},
      uploadOptions: ["video", "photo", "document"],
      isUploadRequired: false,
    },
  } = stepSelected;

  const teamPoints = team ? team.points : 0;

  const { isUploadRequired = false } = settings;

  const dispatch = useDispatch();

  const animatedBoxStyle = useAnimatedStyle(() => {
    return {
      opacity: interpolate(keyboard.height.value, [0, 150], [0, 1]),
      bottom: keyboard.height.value + 10,
    };
  });

  const isDisabled = useCallback(() => {
    return (
      (numberOfFiles === 0 && isUploadRequired) ||
      (!comment && numberOfFiles === 0 && isFeedbackRequired) ||
      (audioIsPlaying && numberOfFiles === 0) ||
      (analysisType !== "text" && numberOfFiles === 0)
    );
  }, [
    numberOfFiles,
    isUploadRequired,
    comment,
    isFeedbackRequired,
    audioIsPlaying,
    analysisType,
  ]);

  useEffect(() => {
    dispatch(
      changeActionState({
        oldTeamPoints: teamPoints,
        oldUserPoints: userPoints,
        oldRank: userRank,
        oldTeamRank: teamRank,
      }),
    );
  }, []);

  const cancelFeedback = () => {
    dispatch(updateFilesToUpload([]));
    dispatch(changeComment(""));
    navigation.goBack();
  };

  useBackHandler(() => {
    if (isFocused) {
      cancelFeedback();
      return true;
    }
    return false;
  });

  const handlePressActionTitle = () => {
    dispatch(changeActionState({ seeActionAgain: true }));
    dispatch(
      selectStep({
        stepSelected,
      }),
    );
    navigation.navigate("Action", {
      isComingFromAnalysis: true,
    });
  };

  const handlePressOutside = () => {
    Keyboard.dismiss();
  };

  const handlePressMainButton = () => {
    navigation.navigate("ActionFeelingScreen");
    setAudioIsPlaying(false);
  };

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsReflexiveAnalysisScreen}>
      <WebAnalysisBoxing
        leftComponent={
          <MySafeAreaView
            flex={1}
            backgroundColor={"neutral.white"}
            borderRadius={isWeb ? "inherit" : 0}
            paddingHorizontal={isWebDesktop ? "webM" : "sl"}
            paddingTop={isWebDesktop ? "null" : "xs"}
            zIndex={0}
            overflow="visible"
          >
            <Pressable onPress={handlePressOutside}>
              {isWebDesktop && (
                <MainTitle
                  variant="l6-bold"
                  marginTop="l"
                  marginBottom={{ smallWeb: "sm", web: "m" }}
                >
                  {i18n.t("reflexive-analysis")}
                </MainTitle>
              )}
              {!isWebDesktop && (
                <>
                  <HeaderScreen
                    title={i18n.t("reflexive-analysis")}
                    leftIcon={isWebDesktop ? undefined : true}
                    callbackOnPress={cancelFeedback}
                  />
                  <MainText
                    style={{ marginTop: -theme.spacing.xs }}
                    variant="l4-bold"
                    color="brand.500"
                    marginBottom="sm"
                  >
                    {selectLanguage({ text: title, language }) || `Action`}
                  </MainText>
                </>
              )}
              {analysisType !== "text" && (
                <Box
                  marginBottom={{
                    phone:
                      numberOfFiles > 0 && analysisType === "video"
                        ? "xxs"
                        : "s",
                  }}
                  marginTop={
                    numberOfFiles > 0 && analysisType === "video" ? "m" : "null"
                  }
                  width="100%"
                >
                  <MainText variant="l4-medium" color="neutral.600">
                    {selectLanguage({
                      text: settings?.uploadInstruction,
                      language,
                    }) ||
                      selectLanguage({ text: debriefText, language }) ||
                      i18n.t("feeling-commentary-desc")}
                  </MainText>
                </Box>
              )}
            </Pressable>
            {analysisType === "video" && <VideoAnalysis />}
            {analysisType === "audio" && (
              <AudioAnalysis
                audioIsPlaying={audioIsPlaying}
                setAudioIsPlaying={setAudioIsPlaying}
              />
            )}
            {analysisType === "text" && (
              <TextAndImageAnalysis
                setIsEditingComment={setIsEditingComment}
                isEditingComment={isEditingComment}
              />
            )}
            {!isWeb && (
              <ReanimatedBox
                zIndex={9}
                position="absolute"
                right={theme.spacing.sl}
                style={animatedBoxStyle}
              >
                <ArrowCircleButton onPress={() => Keyboard.dismiss()} />
              </ReanimatedBox>
            )}
            {(!isEditingComment || isWeb) && (
              <Box width="100%" alignSelf="center">
                <MainButton
                  margin={isWeb ? "l" : "s"}
                  isDisabled={isDisabled()}
                  onPress={handlePressMainButton}
                  text={i18n.t("next")}
                  textColor={
                    isUploadRequired && filesToUpload.length === 0
                      ? "neutral.700"
                      : "neutral.white"
                  }
                />
              </Box>
            )}
          </MySafeAreaView>
        }
        rightComponent={<Action />}
      />
    </WebDesktopView>
  );
};

export default ReflexiveAnalysisScreen;
